import React, { useMemo, useRef, useState } from 'react';
import { FiX, FiUpload } from 'react-icons/fi';
import { recitalService } from '../../../services/RecitalService';
import { parseDurationInput, formatSecs, DEFAULT_CLASS_ACT_SECS } from '../../../utils/recitalDuration';

// Add or edit a ROUTINE: a number that is not on the schedule (opening number,
// finale) but still has dancers on stage. It is saved as a recital-only class
// with its own roster, so it is numbered, quick-change checked and exported like
// any class. The roster has the same three ways in as the Students tab: type the
// names, upload the same XLS, or tick dancers already in this recital.
// Andrew, 2026-10-12. Mockup: recital-unlisted-roster-mockup-v1.html.

const WAYS = [
    { key: 'type', label: 'Type names' },
    { key: 'xls',  label: 'Upload XLS' },
    { key: 'pick', label: 'From this recital' },
];

const S = {
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:200,
        display:'flex', alignItems:'center', justifyContent:'center' },
    dlg: { background:'#fff', borderRadius:12, width:560, maxWidth:'calc(100vw - 32px)', maxHeight:'calc(100vh - 32px)',
        display:'flex', flexDirection:'column', boxShadow:'0 8px 40px rgba(0,0,0,0.18)', overflow:'hidden' },
    head: { padding:'14px 18px 11px', borderBottom:'1px solid #f0f0f8', display:'flex', alignItems:'center', gap:8 },
    title: { fontSize:14, fontWeight:700, color:'#3f3f55' },
    sub: { fontSize:11, color:'#9999bb' },
    body: { padding:'14px 18px 6px', overflowY:'auto' },
    label: { display:'block', fontSize:10, fontWeight:600, color:'#a0a0bc', textTransform:'uppercase', letterSpacing:0.6, marginBottom:5 },
    input: { width:'100%', background:'#f2f3f9', border:'1px solid #d9d9e8', borderRadius:7, padding:'8px 10px',
        fontSize:13, color:'#3f3f55', outline:'none', boxSizing:'border-box' },
    ways: { display:'flex', background:'#f2f3f9', border:'1px solid #d9d9e8', borderRadius:7, overflow:'hidden', marginBottom:10 },
    way: (on) => ({ flex:1, textAlign:'center', fontSize:12, padding:'7px 10px', cursor:'pointer',
        color: on ? '#fff' : '#7a7a99', background: on ? '#29b8e5' : 'transparent', fontWeight: on ? 500 : 400 }),
    pane: { background:'#fafafd', border:'1px solid #e8e8f2', borderRadius:8, padding:10 },
    smallInput: { flex:1, background:'#fff', border:'1px solid #d9d9e8', borderRadius:7, padding:'7px 10px',
        fontSize:12, color:'#3f3f55', outline:'none', fontFamily:'inherit' },
    btn: { fontSize:12, padding:'7px 12px', borderRadius:7, border:'1px solid #d9d9e8', background:'#fff',
        color:'#5a5a7a', cursor:'pointer', whiteSpace:'nowrap' },
    primary: { fontSize:12, padding:'7px 14px', borderRadius:7, border:'1px solid #29b8e5', background:'#29b8e5',
        color:'#fff', cursor:'pointer', fontWeight:500 },
    hint: { fontSize:11, color:'#9999bb', marginTop:6, lineHeight:1.4 },
    roster: { marginTop:12, border:'1px solid #e8e8f2', borderRadius:8, overflow:'hidden', maxHeight:200, overflowY:'auto' },
    rItem: { display:'flex', alignItems:'center', gap:8, padding:'7px 10px', fontSize:12, color:'#3f3f55',
        borderBottom:'1px solid #f5f5fa', background:'#fff' },
    foot: { display:'flex', alignItems:'center', gap:8, padding:'12px 18px 14px', borderTop:'1px solid #f0f0f8' },
    err: { fontSize:11, color:'#d9534f', marginTop:6 },
};

const splitNames = (text) => String(text || '').split(/\r?\n|,|;/).map((s) => s.trim()).filter(Boolean);
const keyOf = (name) => name.trim().replace(/\s+/g, ' ').toLowerCase();

export default function RoutineDialog({ recital, show, position, routine, onClose, onSaved }) {
    const editing = !!routine;
    const [name, setName] = useState(routine?.className || '');
    const [stageTime, setStageTime] = useState(formatSecs(DEFAULT_CLASS_ACT_SECS));
    const [roster, setRoster] = useState(() => (routine?.students || []).map((s) => s.name));
    const [way, setWay] = useState('type');
    const [typed, setTyped] = useState('');
    const [search, setSearch] = useState('');
    const [xlsErrors, setXlsErrors] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const fileRef = useRef();

    // Every dancer already in any class of this recital, once each, with where
    // they dance. The routine being edited is left out of its own list.
    const dancers = useMemo(() => {
        const map = new Map();
        (recital?.classes || []).forEach((cls) => {
            if (routine && cls.id === routine.id) return;
            (cls.students || []).forEach((s) => {
                const clean = String(s.name || '').trim().replace(/\s+/g, ' ');
                if (!clean) return;
                const k = clean.toLowerCase();
                const entry = map.get(k) || { name: clean, where: new Set() };
                entry.where.add(cls.className);
                map.set(k, entry);
            });
        });
        return [...map.values()]
            .map((d) => ({ ...d, where: [...d.where].sort().join(', ') }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [recital, routine]);

    const inRoster = useMemo(() => new Set(roster.map(keyOf)), [roster]);

    const addNames = (names) => {
        setRoster((cur) => {
            const have = new Set(cur.map(keyOf));
            const next = [...cur];
            names.forEach((n) => {
                const clean = n.trim().replace(/\s+/g, ' ');
                if (clean && !have.has(clean.toLowerCase())) { have.add(clean.toLowerCase()); next.push(clean); }
            });
            return next;
        });
    };
    const removeName = (n) => setRoster((cur) => cur.filter((x) => x !== n));

    const addTyped = () => {
        const names = splitNames(typed);
        if (!names.length) return;
        addNames(names);
        setTyped('');
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true); setError(null); setXlsErrors([]);
        try {
            const res = await recitalService.parseRoutineXls(recital.id, file);
            addNames(res.data?.names || []);
            setXlsErrors(res.data?.errors || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not read that file.');
        } finally { setBusy(false); }
    };

    const visibleDancers = dancers.filter((d) => !search || d.name.toLowerCase().includes(search.toLowerCase()));
    const togglePick = (dancerName) => {
        const k = keyOf(dancerName);
        if (inRoster.has(k)) { removeName(roster.find((r) => keyOf(r) === k)); return; }
        addNames([dancerName]);
    };
    const pickAllShown = () => addNames(visibleDancers.map((d) => d.name));

    const save = async () => {
        const trimmed = name.trim();
        if (!trimmed) { setError('Give the routine a name.'); return; }
        setBusy(true); setError(null);
        try {
            if (editing) {
                await recitalService.updateRoutine(routine.id, { className: trimmed, students: roster });
            } else {
                const secs = parseDurationInput(stageTime);
                await recitalService.createRoutine(recital.id, {
                    className: trimmed, students: roster, showId: show?.id ?? null, position,
                    durationSecs: secs ?? DEFAULT_CLASS_ACT_SECS,
                });
            }
            await onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Could not save. Please try again.');
        } finally { setBusy(false); }
    };

    return (
        <div style={S.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={S.dlg} role="dialog" aria-label={editing ? 'Edit routine' : 'New routine'}>
                <div style={S.head}>
                    <div>
                        <div style={S.title}>{editing ? 'Edit routine' : 'New routine'}</div>
                        <div style={S.sub}>Not on the schedule. Lives only in this recital.</div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close"
                        style={{ marginLeft:'auto', background:'none', border:'none', color:'#c8c8e0', cursor:'pointer', display:'flex' }}>
                        <FiX size={16}/>
                    </button>
                </div>

                <div style={S.body}>
                    <div style={{ display:'flex', gap:10 }}>
                        <div style={{ flex:1 }}>
                            <label style={S.label}>Name</label>
                            <input autoFocus style={S.input} value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Opening Number" />
                        </div>
                        {!editing && (
                            <div style={{ flex:'0 0 120px' }}>
                                <label style={S.label}>Stage time</label>
                                <input style={S.input} value={stageTime} onChange={(e) => setStageTime(e.target.value)} placeholder="2:30" />
                            </div>
                        )}
                    </div>

                    <div style={{ display:'flex', alignItems:'center', margin:'16px 0 8px' }}>
                        <span style={{ ...S.label, marginBottom:0 }}>Roster</span>
                        <span style={{ marginLeft:'auto', fontSize:11, color:'#9999bb' }}>
                            {roster.length} dancer{roster.length === 1 ? '' : 's'}
                        </span>
                    </div>

                    <div style={S.ways}>
                        {WAYS.map((w) => (
                            <div key={w.key} style={S.way(way === w.key)} onClick={() => setWay(w.key)}>{w.label}</div>
                        ))}
                    </div>

                    {way === 'type' && (
                        <div style={S.pane}>
                            <div style={{ display:'flex', gap:8 }}>
                                <textarea rows={1} style={{ ...S.smallInput, resize:'vertical', minHeight:32 }} value={typed}
                                    onChange={(e) => setTyped(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addTyped(); } }}
                                    placeholder="Dancer name, then Enter" />
                                <button type="button" style={S.btn} onClick={addTyped}>Add</button>
                            </div>
                            <div style={S.hint}>Paste a whole list here too, one name per line. They all get added.</div>
                        </div>
                    )}

                    {way === 'xls' && (
                        <div style={S.pane}>
                            <div style={{ border:'1.5px dashed #c8c8e0', borderRadius:8, padding:16, textAlign:'center', background:'#fff' }}>
                                <div style={{ fontSize:12, fontWeight:500, color:'#3f3f55' }}>Upload an .xls or .xlsx</div>
                                <div style={{ fontSize:11, color:'#9999bb', marginTop:3 }}>
                                    Same file as the Students tab: a <b>Name</b> column. Birth dates are ignored here. First sheet only.
                                </div>
                                <input ref={fileRef} type="file" accept=".xls,.xlsx" style={{ display:'none' }} onChange={onFile} />
                                <button type="button" style={{ ...S.btn, marginTop:10, display:'inline-flex', alignItems:'center', gap:6 }}
                                    disabled={busy} onClick={() => fileRef.current?.click()}>
                                    <FiUpload size={12}/> {busy ? 'Reading...' : 'Choose file'}
                                </button>
                            </div>
                            {xlsErrors.length > 0 && (
                                <div style={S.hint}>Skipped: {xlsErrors.join(' · ')}</div>
                            )}
                        </div>
                    )}

                    {way === 'pick' && (
                        <div style={S.pane}>
                            <input style={{ ...S.smallInput, width:'100%', boxSizing:'border-box', marginBottom:8 }} value={search}
                                onChange={(e) => setSearch(e.target.value)} placeholder="Search dancers in this recital" />
                            <div style={{ maxHeight:170, overflowY:'auto', border:'1px solid #e8e8f2', borderRadius:7, background:'#fff' }}>
                                {visibleDancers.length === 0 && (
                                    <div style={{ padding:12, fontSize:11, color:'#9999bb', textAlign:'center' }}>
                                        {dancers.length === 0 ? 'No dancers in this recital yet.' : 'No one matches that search.'}
                                    </div>
                                )}
                                {visibleDancers.map((d) => {
                                    const on = inRoster.has(keyOf(d.name));
                                    return (
                                        <label key={d.name} style={{ display:'flex', flexDirection:'row', alignItems:'center', gap:9, padding:'6px 10px', fontSize:12,
                                            color:'#3f3f55', borderBottom:'1px solid #f5f5fa', cursor:'pointer', margin:0, width:'100%', boxSizing:'border-box', fontWeight:400 }}>
                                            <input type="checkbox" checked={on} onChange={() => togglePick(d.name)}
                                                style={{ accentColor:'#29b8e5', width:14, height:14, minWidth:14, margin:0, padding:0, flex:'0 0 14px', appearance:'auto', WebkitAppearance:'checkbox' }} />
                                            <span style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.name}</span>
                                            <span style={{ marginLeft:'auto', fontSize:10, color: on ? '#2ab568' : '#9999bb', whiteSpace:'nowrap',
                                                overflow:'hidden', textOverflow:'ellipsis', maxWidth:220 }}>{d.where}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <div style={{ display:'flex', alignItems:'center', marginTop:8, fontSize:11, color:'#9999bb' }}>
                                Everyone already in a class of this recital.
                                <button type="button" onClick={pickAllShown} disabled={!visibleDancers.length}
                                    style={{ marginLeft:'auto', background:'none', border:'none', color:'#29b8e5', cursor:'pointer', fontSize:11 }}>
                                    Select all shown
                                </button>
                            </div>
                        </div>
                    )}

                    <div style={S.roster}>
                        {roster.length === 0 ? (
                            <div style={{ padding:14, textAlign:'center', fontSize:11, color:'#9999bb', background:'#fff' }}>
                                No dancers yet. Add them above.
                            </div>
                        ) : roster.map((n) => (
                            <div key={n} style={S.rItem}>
                                {n}
                                <button type="button" onClick={() => removeName(n)} aria-label={`Remove ${n}`}
                                    style={{ marginLeft:'auto', background:'none', border:'none', color:'#c8c8e0', cursor:'pointer', display:'flex' }}>
                                    <FiX size={12}/>
                                </button>
                            </div>
                        ))}
                    </div>
                    {error && <div style={S.err}>{error}</div>}
                </div>

                <div style={S.foot}>
                    <div style={{ fontSize:11, color:'#9999bb', lineHeight:1.4 }}>
                        Counts as an act. Quick changes and family checks include these dancers.
                    </div>
                    <button type="button" style={{ ...S.btn, marginLeft:'auto' }} onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" style={S.primary} onClick={save} disabled={busy}>
                        {busy ? 'Saving...' : editing ? 'Save' : 'Add to show'}
                    </button>
                </div>
            </div>
        </div>
    );
}
